import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useData } from "@/lib/dataStore";
import api from "@/lib/api";
import { formatSEK, todayISO } from "@/lib/utils-app";
import { exportReceiptsToExcel, shareOrDownload } from "@/lib/exportXlsx";
import { Printer, ReceiptX, Plus, Trash, ShareNetwork, DownloadSimple, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";

const PAYMENT_METHODS = ["Swish", "Card", "Invoice"];
const GRASS = ["Wheat", "Barley", "Corn"];
const CUSTOMER_TYPE = ["Human Food", "Animal Feed/Other"];
const VAT_OPTIONS = [
  { value: 0.25, label: "25% — Standard" },
  { value: 0.12, label: "12% — Agricultural / Food" },
  { value: 0.06, label: "6% — Reduced (Human Food)" },
];
// Grain → Grass conversion ratios. Value N means 1 kg dry grain = N kg grass.
const CONVERSION_RATIOS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Grain → Grass conversion. User enters BOTH sides (e.g., 1.2 : 5.1) — decimals allowed.
// Effective multiplier = grass / grain (e.g., 5.1 / 1.2 = 4.25 kg grass per kg grain).
// Grain deducted from inventory = weight_kg / effective_multiplier.

const initialForm = () => ({
  date: todayISO(),
  payment_method: "Swish",
  customer_name: "",
  customer_email: "",
  weight_kg: "",
  price_per_kg: "",
  vat_rate: 0.25,
  conversion_grain: 1,
  conversion_grass: 5,
  language: "sv",
  grass_variety: "Barley",
  customer_type: "Animal Feed/Other",
  quantity_trays: "",
});

export default function ReceiptPage() {
  const { receipts, settings, refresh } = useData();
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);
  const [activeReceipt, setActiveReceipt] = useState(null); // last created / preview
  const [bulkRange, setBulkRange] = useState("month");
  const printRef = useRef(null);

  const subtotal = useMemo(() => {
    const w = parseFloat(form.weight_kg) || 0;
    const p = parseFloat(form.price_per_kg) || 0;
    return +(w * p).toFixed(2);
  }, [form.weight_kg, form.price_per_kg]);
  const vat = useMemo(() => +(subtotal * form.vat_rate).toFixed(2), [subtotal, form.vat_rate]);
  const total = useMemo(() => +(subtotal + vat).toFixed(2), [subtotal, vat]);

  // Auto-calculate trays from weight & conversion ratio.
  // Formula: trays = weight_kg / conversion_grass (each tray produces `conversion_grass` kg).
  // User can manually override (e.g. round up to whole tray count → also bump the weight).
  const traysManuallyEditedRef = useRef(false);
  useEffect(() => {
    if (traysManuallyEditedRef.current) return;
    const w = parseFloat(form.weight_kg);
    const grass = parseFloat(form.conversion_grass);
    if (!isNaN(w) && !isNaN(grass) && w > 0 && grass > 0) {
      const calc = +(w / grass).toFixed(2);
      if (String(calc) !== form.quantity_trays) {
        setForm((f) => ({ ...f, quantity_trays: String(calc) }));
      }
    }
  }, [form.weight_kg, form.conversion_grass, form.quantity_trays]);

  // Suggested whole-tray rounding hint
  const wholeTrayHint = useMemo(() => {
    const w = parseFloat(form.weight_kg);
    const grass = parseFloat(form.conversion_grass);
    if (isNaN(w) || isNaN(grass) || w <= 0 || grass <= 0) return null;
    const exact = w / grass;
    const ceil = Math.ceil(exact);
    if (exact === ceil) return null; // already whole
    const suggestedWeight = +(ceil * grass).toFixed(2);
    return { exact: +exact.toFixed(2), ceil, suggestedWeight };
  }, [form.weight_kg, form.conversion_grass]);

  const issue = async () => {
    if (!form.weight_kg || !form.price_per_kg) {
      toast.error("Enter weight and price per kg");
      return;
    }
    setSaving(true);
    try {
      const grainNum = parseFloat(form.conversion_grain) || 1;
      const grassNum = parseFloat(form.conversion_grass) || 5;
      const payload = {
        date: form.date,
        payment_method: form.payment_method,
        customer_name: form.customer_name || "",
        customer_email: form.customer_email || "",
        item_name: "Premium Hydroponic Green Grass Feed",
        weight_kg: parseFloat(form.weight_kg) || 0,
        price_per_kg: parseFloat(form.price_per_kg) || 0,
        vat_rate: form.vat_rate,
        conversion_grain: grainNum,
        conversion_grass: grassNum,
        conversion_ratio: +(grassNum / grainNum).toFixed(4),
        language: form.language,
        grass_variety: form.grass_variety,
        customer_type: form.customer_type,
        quantity_trays: parseFloat(form.quantity_trays) || 0,
      };
      const { data } = await api.post("/receipts", payload);
      toast.success(`Receipt ${data.number} issued — sale logged automatically`);
      setActiveReceipt(data);
      setForm(initialForm());
      traysManuallyEditedRef.current = false;
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to issue receipt");
    } finally {
      setSaving(false);
    }
  };

  const removeReceipt = async (id) => {
    if (!window.confirm("Delete this receipt?")) return;
    await api.delete(`/receipts/${id}`);
    if (activeReceipt?.id === id) setActiveReceipt(null);
    refresh();
  };

  // ---- Void / cancel a transaction (Bokföringslagen-compliant) ----
  const [voidTarget, setVoidTarget] = useState(null); // receipt object or null
  const [voidReason, setVoidReason] = useState("incorrect_entry");
  const [voidReasonText, setVoidReasonText] = useState("");
  const [voidSubmitting, setVoidSubmitting] = useState(false);

  const openVoidDialog = (r) => {
    setVoidTarget(r);
    setVoidReason("incorrect_entry");
    setVoidReasonText("");
  };
  const closeVoidDialog = () => {
    if (voidSubmitting) return;
    setVoidTarget(null);
  };
  const submitVoid = async () => {
    if (!voidTarget) return;
    if (voidReason === "other" && !voidReasonText.trim()) {
      toast.error("Please type a reason in the 'Other' field");
      return;
    }
    setVoidSubmitting(true);
    try {
      const { data } = await api.post(`/receipts/${voidTarget.id}/void`, {
        reason: voidReason,
        reason_text: voidReasonText.trim(),
      });
      toast.success(`Receipt ${voidTarget.number || voidTarget.id} cancelled · ${data?.credit_note_number || "Credit note issued"}`);
      setVoidTarget(null);
      refresh();
      // Auto-download the Kreditfaktura so the user has the audit-paired PDF immediately.
      try {
        await downloadCreditNote({ ...voidTarget, ...data });
      } catch (e) {
        // Non-fatal — user can re-download from the row's button.
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel transaction");
    } finally {
      setVoidSubmitting(false);
    }
  };

  const downloadCreditNote = async (r) => {
    try {
      const res = await api.get(`/receipts/${r.id}/credit-note/pdf`, { responseType: "blob" });
      const idTag = (r.credit_note_number || r.number || r.id).replace(/[^A-Za-z0-9_-]/g, "_");
      const filename = `CreditNote_${idTag}.pdf`;
      // Use the same helper as the Excel export so behaviour is identical:
      // iPhone/iPad → native Share Sheet (Save to Files / iCloud)
      // Desktop / PC → native "Save as…" download dialog
      await shareOrDownload(res.data, filename, "Kreditfaktura", `Credit note ${r.credit_note_number || ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not download credit note");
    }
  };

  const printReceipt = (r) => {
    setActiveReceipt(r);
    setTimeout(() => window.print(), 100);
  };

  const fetchReceiptPdf = async (r) => {
    // axios `api` already has Authorization; fetch the binary PDF
    const res = await api.get(`/receipts/${r.id}/pdf`, { responseType: "blob" });
    return res.data; // Blob
  };

  // One unified Download PDF helper.
  // Desktop (Windows / macOS) → native "Save as…" dialog
  // iPhone / iPad             → native Share Sheet → Save to Files / iCloud Drive
  const downloadReceiptPdf = async (r) => {
    try {
      const blob = await fetchReceiptPdf(r);
      const idTag = (r.number || r.id).replace(/[^A-Za-z0-9_-]/g, "_");
      const filename = `CashInvoice_${idTag}.pdf`;
      await shareOrDownload(blob, filename, "Kontantfaktura", `Cash invoice ${r.number || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download the cash invoice");
    }
  };

  // Bulk download
  const filterByRange = (range) => {
    const now = new Date();
    const end = todayISO();
    let start;
    if (range === "week") {
      const d = new Date(now); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day);
      start = d.toISOString().slice(0, 10);
    } else if (range === "month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    } else if (range === "quarter") {
      const q = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
    } else if (range === "year") {
      start = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
    } else {
      start = null;
    }
    return receipts.filter((r) => (!start || r.date >= start) && r.date <= end);
  };

  const bulkExport = async () => {
    const filtered = filterByRange(bulkRange);
    if (filtered.length === 0) {
      toast.error("No receipts in this period");
      return;
    }
    const result = await exportReceiptsToExcel(filtered, bulkRange);
    if (result.shared) toast.success(`Shared ${filtered.length} receipt(s)`);
    else toast.success(`Downloaded ${filtered.length} receipt(s)`);
  };

  const backfillSales = async () => {
    if (!window.confirm("Auto-create matching Sale rows for every receipt that doesn't have one yet?\n\nThis populates the Logs, Tax and Profit dashboards retroactively.")) return;
    try {
      const { data } = await api.post("/receipts/backfill-sales");
      toast.success(`Backfilled ${data.backfilled} sale(s) — refresh Logs/Tax/Profit`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Tab 1</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">Invoice</h1>
        <p className="text-sm text-slate-500 mt-1">Kontantfaktura · Issue, print &amp; download customer invoices · VAT 25% / 12% / 6%</p>
      </div>

      {/* Action bar */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => activeReceipt && printReceipt(activeReceipt)}
              disabled={!activeReceipt}
              className="bg-slate-800 hover:bg-slate-900 text-white"
              data-testid="print-receipt-button"
            >
              <Printer size={18} weight="duotone" className="mr-1.5" /> Print
            </Button>
            <Button
              onClick={() => activeReceipt && downloadReceiptPdf(activeReceipt)}
              disabled={!activeReceipt}
              className="bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="download-receipt-pdf-button"
              title="Download PDF — Windows / macOS save dialog, iPhone/iPad Share Sheet"
            >
              <DownloadSimple size={18} weight="duotone" className="mr-1.5" />
              Download PDF
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={backfillSales}
              variant="outline"
              className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              data-testid="backfill-sales-button"
              title="Create Sale rows for receipts issued before sale-linking was enabled"
            >
              Backfill Sales
            </Button>
            <Select value={bulkRange} onValueChange={setBulkRange}>
              <SelectTrigger className="w-[140px] h-9" data-testid="bulk-range-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Weekly</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="quarter">Quarterly</SelectItem>
                <SelectItem value="year">Yearly</SelectItem>
                <SelectItem value="all">All-time</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={bulkExport}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="bulk-download-button"
            >
              <ShareNetwork size={18} weight="duotone" className="mr-1.5" /> Bulk Download
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Issue form */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ReceiptX size={20} weight="duotone" className="text-emerald-800" />
              Issue New Receipt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="grid grid-cols-2 gap-3" onSubmit={(e) => { e.preventDefault(); issue(); }} data-testid="receipt-form">
              <div>
                <Label className="text-slate-700">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="receipt-date-input" />
              </div>
              <div>
                <Label className="text-slate-700">Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-1" data-testid="receipt-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-slate-700">Customer Name (optional)</Label>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1" data-testid="receipt-customer-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-slate-700">Customer Email (optional)</Label>
                <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="customer@example.com" className="mt-1" data-testid="receipt-customer-email" />
              </div>
              <div>
                <Label className="text-slate-700">Total Weight (kg)</Label>
                <Input type="number" step="0.01" min="0" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="mt-1" data-testid="receipt-weight-input" />
              </div>
              <div>
                <Label className="text-slate-700">Price / kg (SEK)</Label>
                <Input type="number" step="0.01" min="0" value={form.price_per_kg} onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })} className="mt-1" data-testid="receipt-price-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-slate-700">VAT / Moms Rate</Label>
                <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: parseFloat(v) })}>
                  <SelectTrigger className="mt-1" data-testid="receipt-vat-rate-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VAT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)} data-testid={`receipt-vat-option-${Math.round(opt.value * 100)}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Choose per transaction · 25% Standard · 12% Restaurants/Hotels · 6% Food &amp; agricultural produce</p>
              </div>
              <div className="col-span-2">
                <Label className="text-slate-700">Conversion Ratio (Grain → Grass)</Label>
                <div className="mt-1 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                  <Input
                    type="number" step="0.01" min="0.01"
                    value={form.conversion_grain}
                    onChange={(e) => setForm({ ...form, conversion_grain: e.target.value })}
                    placeholder="1"
                    data-testid="receipt-conversion-grain-input"
                    className="text-center"
                  />
                  <span className="text-slate-500 font-semibold text-sm">kg grain :</span>
                  <Input
                    type="number" step="0.01" min="0.01"
                    value={form.conversion_grass}
                    onChange={(e) => setForm({ ...form, conversion_grass: e.target.value })}
                    placeholder="5"
                    data-testid="receipt-conversion-grass-input"
                    className="text-center"
                  />
                </div>
                {(() => {
                  const w = parseFloat(form.weight_kg) || 0;
                  const grain = parseFloat(form.conversion_grain) || 0;
                  const grass = parseFloat(form.conversion_grass) || 0;
                  if (grain <= 0 || grass <= 0) {
                    return <p className="text-[11px] text-amber-700 mt-1">Enter both sides of the ratio (decimals allowed)</p>;
                  }
                  const mult = grass / grain;
                  const grainUsed = w > 0 ? +(w / mult).toFixed(2) : 0;
                  return (
                    <p className="text-[11px] text-emerald-800 mt-1" data-testid="receipt-grain-deduction-hint">
                      1 kg grain → <span className="font-semibold">{mult.toFixed(2)} kg</span> grass
                      {w > 0
                        ? <> · {w} kg grass needs <span className="font-semibold">{grainUsed} kg</span> of <span className="font-semibold">{form.grass_variety}</span> grain — deducted from inventory</>
                        : <> · Enter total weight above to see grain deduction</>
                      }
                    </p>
                  );
                })()}
              </div>
              <div className="col-span-2">
                <Label className="text-slate-700">Receipt Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger className="mt-1" data-testid="receipt-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en" data-testid="receipt-language-en">English</SelectItem>
                    <SelectItem value="sv" data-testid="receipt-language-sv">Svenska</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Affects printed &amp; PDF receipt — switch per customer</p>
              </div>
              <div className="col-span-2 pt-3 mt-1 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-bold mb-2">Sale Linking (auto-creates a sale row for Tax &amp; Profit)</div>
              </div>
              <div>
                <Label className="text-slate-700">Grain Variety (deducts from inventory)</Label>
                <Select value={form.grass_variety} onValueChange={(v) => setForm({ ...form, grass_variety: v })}>
                  <SelectTrigger className="mt-1" data-testid="receipt-variety-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GRASS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Customer Type</Label>
                <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="receipt-customer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_TYPE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-slate-700 flex items-center gap-1.5">
                  Trays Sold
                  {!traysManuallyEditedRef.current && (
                    <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">Auto</span>
                  )}
                </Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.quantity_trays}
                  onChange={(e) => { traysManuallyEditedRef.current = true; setForm({ ...form, quantity_trays: e.target.value }); }}
                  placeholder="0"
                  className="mt-1"
                  data-testid="receipt-trays-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Auto-filled from <span className="font-medium">weight ÷ {form.conversion_grass || "?"} kg per tray</span>. Edit to override.
                </p>
                {wholeTrayHint && (
                  <button
                    type="button"
                    onClick={() => {
                      traysManuallyEditedRef.current = false;
                      setForm((f) => ({ ...f, weight_kg: String(wholeTrayHint.suggestedWeight), quantity_trays: String(wholeTrayHint.ceil) }));
                    }}
                    data-testid="receipt-round-up-trays"
                    className="mt-1.5 w-full text-left rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 hover:bg-amber-100 transition-colors"
                  >
                    Round up to <span className="font-semibold">{wholeTrayHint.ceil} whole trays</span> ({wholeTrayHint.suggestedWeight} kg) ?
                    <span className="block text-[10px] text-amber-700 mt-0.5">Currently {wholeTrayHint.exact} trays — tap to set whole-tray amounts</span>
                  </button>
                )}
              </div>
              <div className="col-span-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
                <SummaryRow label="Subtotal (Excl. VAT)" value={formatSEK(subtotal)} />
                <SummaryRow label={`VAT / Moms (${(form.vat_rate * 100).toFixed(0)}%)`} value={formatSEK(vat)} accent="red" />
                <SummaryRow label="TOTAL TO PAY (Incl. VAT)" value={formatSEK(total)} bold accent="emerald" />
              </div>
              <div className="col-span-2">
                <Button type="submit" disabled={saving} className="w-full bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="receipt-submit-button">
                  <Plus size={18} weight="bold" className="mr-1.5" />
                  {saving ? "Issuing…" : "Issue Receipt"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Receipt preview */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            {activeReceipt ? (
              <ReceiptDocument receipt={activeReceipt} settings={settings} ref={printRef} />
            ) : (
              <div className="text-center py-12 text-slate-500 text-sm">
                Issue a receipt to preview it here. Receipt number auto-generates as <code className="bg-slate-100 px-1 rounded">#YYYY-NNNN</code>.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent receipts list */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg">Recent Receipts</CardTitle>
            <Badge variant="secondary" className="bg-slate-100 text-slate-700">
              Last 10 · {receipts.length} total
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {receipts.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">No receipts yet</div>
          ) : (
            <ul className="divide-y divide-slate-200" data-testid="receipts-list">
              {receipts.slice(0, 10).map((r) => {
                const isVoided = !!r.voided;
                const reasonLabel = ({
                  incorrect_entry: "Incorrect entry",
                  customer_cancelled: "Customer cancelled",
                  other: "Other",
                })[r.void_reason] || r.void_reason;
                return (
                <li
                  key={r.id}
                  className={`py-3 flex items-center justify-between gap-3 ${isVoided ? "opacity-80" : ""}`}
                  data-testid={`receipt-row-${r.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`font-heading font-semibold ${isVoided ? "text-slate-500 line-through" : "text-slate-900"}`}
                        data-testid={`receipt-number-${r.number}`}
                      >
                        {r.number}
                      </span>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{r.payment_method}</Badge>
                      {isVoided && (
                        <Badge
                          variant="outline"
                          className="bg-red-50 text-red-700 border-red-300 font-bold"
                          data-testid={`receipt-voided-${r.id}`}
                        >
                          VOIDED
                        </Badge>
                      )}
                      {r.customer_name ? <span className="text-sm text-slate-600 truncate">{r.customer_name}</span> : null}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {r.date} · {r.weight_kg} kg × {r.price_per_kg} SEK/kg
                    </div>
                    {isVoided && (
                      <div className="text-[11px] text-red-700 mt-1" data-testid={`receipt-void-reason-${r.id}`}>
                        Cancelled · {reasonLabel}{r.void_reason === "other" && r.void_reason_text ? `: "${r.void_reason_text}"` : ""}
                        {r.voided_at ? ` · ${new Date(r.voided_at).toLocaleString("sv-SE")}` : ""}
                        {r.credit_note_number ? <> · <span className="font-semibold">{r.credit_note_number}</span></> : null}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className={`font-heading font-semibold ${isVoided ? "text-slate-400 line-through" : "text-emerald-800"}`}
                    >
                      {formatSEK(r.total)}
                    </div>
                    {isVoided && (
                      <div className="text-xs font-bold text-red-700" data-testid={`receipt-effective-${r.id}`}>
                        Effective: {formatSEK(0)}
                      </div>
                    )}
                    <div className="flex items-center gap-2 justify-end text-xs mt-1 flex-wrap">
                      <button onClick={() => { setActiveReceipt(r); printReceipt(r); }} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                        <Printer size={14} /> Print
                      </button>
                      <button onClick={() => downloadReceiptPdf(r)} className="text-slate-500 hover:text-blue-700 inline-flex items-center gap-1" title="Download cash invoice PDF" data-testid={`download-receipt-pdf-${r.id}`}>
                        <DownloadSimple size={14} weight="duotone" /> Download
                      </button>
                      {!isVoided && (
                        <button
                          onClick={() => openVoidDialog(r)}
                          className="text-amber-700 hover:text-red-700 inline-flex items-center gap-1 font-medium"
                          data-testid={`void-receipt-${r.id}`}
                          title="Cancel this transaction with a reason (keeps audit trail)"
                        >
                          <Prohibit size={14} weight="duotone" /> Incorrect Transaction
                        </button>
                      )}
                      {isVoided && (
                        <button
                          onClick={() => downloadCreditNote(r)}
                          className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 font-semibold"
                          data-testid={`credit-note-pdf-${r.id}`}
                          title="Download Kreditfaktura (Credit Note) PDF"
                        >
                          <ReceiptX size={14} weight="duotone" /> Credit Note PDF
                        </button>
                      )}
                      <button onClick={() => removeReceipt(r.id)} className="text-slate-400 hover:text-red-600 inline-flex items-center gap-1">
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                </li>
              );})}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Void / Cancel Transaction Dialog */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!o) closeVoidDialog(); }}>
        <DialogContent className="max-w-md" data-testid="void-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel transaction</DialogTitle>
            <DialogDescription>
              {voidTarget ? (
                <>
                  Receipt <span className="font-semibold text-slate-900">{voidTarget.number}</span>
                  {" "}for <span className="font-semibold text-slate-900">{formatSEK(voidTarget.total)}</span>.
                  The entry stays in the ledger (audit trail) but its value will be counted as <strong>0 SEK</strong>.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Reason</Label>
              <RadioGroup
                value={voidReason}
                onValueChange={setVoidReason}
                className="mt-2 space-y-2"
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="incorrect_entry" id="void-r-incorrect" data-testid="void-reason-incorrect" />
                  <span className="text-sm text-slate-800">Incorrect entry</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="customer_cancelled" id="void-r-customer" data-testid="void-reason-customer" />
                  <span className="text-sm text-slate-800">Customer cancelled the transaction</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="other" id="void-r-other" data-testid="void-reason-other" />
                  <span className="text-sm text-slate-800">Other</span>
                </label>
              </RadioGroup>
            </div>

            {voidReason === "other" && (
              <div>
                <Label htmlFor="void-reason-text" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">
                  Reason
                </Label>
                <Textarea
                  id="void-reason-text"
                  value={voidReasonText}
                  onChange={(e) => setVoidReasonText(e.target.value)}
                  placeholder="Type the reason for cancellation…"
                  className="mt-1"
                  rows={3}
                  data-testid="void-reason-text"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeVoidDialog}
              disabled={voidSubmitting}
              data-testid="void-cancel-btn"
            >
              Keep transaction
            </Button>
            <Button
              type="button"
              onClick={submitVoid}
              disabled={voidSubmitting || (voidReason === "other" && !voidReasonText.trim())}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="void-confirm-btn"
            >
              {voidSubmitting ? "Cancelling…" : "Confirm cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden print container */}
      <div className="hidden print:block">
        {activeReceipt && <ReceiptDocument receipt={activeReceipt} settings={settings} forPrint />}
      </div>
    </div>
  );
}

const ReceiptDocument = React.forwardRef(function ReceiptDocument({ receipt, settings, forPrint }, ref) {
  const r = receipt;
  const isSv = (r.language || "en") === "sv";
  const t = {
    receipt: isSv ? "Kvitto" : "Receipt",
    dateTime: isSv ? "Datum & tid" : "Date & Time",
    company: isSv ? "Företag" : "Company",
    customer: isSv ? "Kund" : "Customer",
    orgNr: "Org. Nr",
    momsId: isSv ? "Moms-reg. Nr" : "Moms ID",
    payment: isSv ? "Betalning" : "Payment",
    orderDetails: isSv ? "Orderdetaljer" : "Order Details",
    itemDefault: isSv ? "Premium Hydroponiskt Gräsfoder" : "Premium Hydroponic Grass Feed",
    subtotal: isSv ? "Delsumma (Exkl. moms)" : "Subtotal (Excl. VAT)",
    vat: isSv ? "Moms" : "VAT / Moms",
    total: isSv ? "ATT BETALA (Inkl. moms)" : "TOTAL TO PAY (Incl. VAT)",
    thanks: isSv ? "Tack för ditt köp!" : "Tack för ditt köp! · Thank you for your purchase.",
    vatApplied: isSv ? `Moms tillämpad: ${(r.vat_rate * 100).toFixed(0)}% svensk moms` : `VAT applied: ${(r.vat_rate * 100).toFixed(0)}% Swedish Moms`,
  };
  return (
    <div ref={ref} className={`text-sm ${forPrint ? "receipt-print" : "border border-slate-200 rounded-lg p-4 bg-white"}`} data-testid="receipt-preview">
      <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-slate-200">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">{t.receipt}</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900" data-testid="receipt-number-preview">{r.number}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">{t.dateTime}</div>
          <div className="text-slate-900">{r.date}</div>
          <div className="text-xs text-slate-500">{new Date(r.created_at || Date.now()).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{t.company}</div>
          <div className="font-semibold text-slate-900">{settings?.business_name || "Your Business Name"}</div>
          <div className="text-xs text-slate-600">{t.orgNr}: {settings?.org_number || "—"}</div>
          {settings?.moms_id ? (
            <div className="text-xs text-slate-600" data-testid="receipt-moms-id">{t.momsId}: {settings.moms_id}</div>
          ) : null}
          <div className="text-xs text-slate-600 whitespace-pre-line">{settings?.address || "Sweden"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{t.customer}</div>
          <div className="font-semibold text-slate-900">{r.customer_name || "—"}</div>
          <div className="text-xs text-slate-600">{r.customer_email || ""}</div>
          <div className="text-xs text-slate-600 mt-1">{t.payment}: <span className="font-medium text-slate-900">{r.payment_method}</span></div>
        </div>
      </div>

      <div className="rounded-md bg-slate-50 border border-slate-200 p-3 mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-bold">{t.orderDetails}</div>
        <div className="font-medium text-slate-900 mt-1">{r.item_name || t.itemDefault}</div>
        <div className="text-xs text-slate-600 mt-0.5">{r.weight_kg} kg × {formatSEK(r.price_per_kg)} / kg</div>
      </div>

      <div className="space-y-1.5">
        <SummaryRow label={t.subtotal} value={formatSEK(r.subtotal)} />
        <SummaryRow label={`${t.vat} (${(r.vat_rate * 100).toFixed(0)}%)`} value={formatSEK(r.vat_amount)} accent="red" />
        <div className="border-t border-slate-200 my-1.5" />
        <SummaryRow label={t.total} value={formatSEK(r.total)} bold accent="emerald" />
      </div>

      <div className="mt-4 pt-3 border-t border-slate-200 text-[10px] text-slate-500 text-center">
        {t.thanks} · {t.vatApplied}
      </div>
    </div>
  );
});

const SummaryRow = ({ label, value, bold, accent }) => {
  const accents = {
    red: "text-red-700",
    emerald: "text-emerald-800",
  };
  return (
    <div className="flex items-center justify-between">
      <span className={`${bold ? "font-heading font-bold text-slate-900" : "text-slate-600"}`}>{label}</span>
      <span className={`${bold ? "font-heading font-bold text-lg" : "font-medium"} ${accents[accent] || "text-slate-900"}`}>{value}</span>
    </div>
  );
};
